// Tiny fetch wrapper. Browser cookies carry auth so all requests are
// `credentials: 'same-origin'`. Throws ApiHttpError with status + parsed
// JSON body on non-2xx. Throws ApiNetworkError on transport failure.

import type {
  AdminListUser,
  AdminsListResponse,
  AdminUpdatePayload,
  ApiError,
  Checkin,
  CheckinUpdatePayload,
  CreatePersonTimeOffPayload,
  Holiday,
  HolidayCreatePayload,
  HolidayUpdatePayload,
  HolidaysResponse,
  MeResponse,
  MyStatsResponse,
  PersonSnapshot,
  RangeSnapshot,
  SnapshotPayload,
  SystemStatus,
  TimeOffPayload,
} from './types'

export class ApiHttpError extends Error {
  status: number
  body: ApiError | unknown

  constructor(status: number, body: unknown) {
    super(`HTTP ${status}`)
    this.name = 'ApiHttpError'
    this.status = status
    this.body = body
  }
}

export class ApiNetworkError extends Error {
  constructor(public override readonly cause: unknown) {
    super('network error')
    this.name = 'ApiNetworkError'
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let resp: Response
  try {
    resp = await fetch(path, {
      credentials: 'same-origin',
      headers: { accept: 'application/json', ...(init.headers || {}) },
      ...init,
    })
  } catch (err) {
    throw new ApiNetworkError(err)
  }

  const text = await resp.text()
  const body = text ? safeJson(text) : null

  if (!resp.ok) {
    throw new ApiHttpError(resp.status, body)
  }

  return body as T
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

export const api = {
  getMe: () => request<MeResponse>('/api/v1/me'),
  getMyStats: () => request<MyStatsResponse>('/api/v1/me/stats'),
  getSnapshotToday: () => request<SnapshotPayload>('/api/v1/snapshot/today'),
  getSnapshotRange: (from: string, to: string, tz: string) => {
    const qs = new URLSearchParams({ from, to, tz }).toString()
    return request<RangeSnapshot>(`/api/v1/snapshot/range?${qs}`)
  },
  postBackfillRequest: (userId: string, date: string) =>
    request<{ id: string; requested_at: string }>('/api/v1/backfill_requests', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ user_id: userId, date }),
    }),
  getPerson: (slackId: string, days: number, tz: string) => {
    const qs = new URLSearchParams({ days: String(days), tz }).toString()
    return request<PersonSnapshot>(
      `/api/v1/people/${encodeURIComponent(slackId)}?${qs}`
    )
  },
  signOut: () => request<void>('/api/v1/session', { method: 'DELETE' }),

  listHolidays: () => request<HolidaysResponse>('/api/v1/holidays'),

  createHoliday: (payload: HolidayCreatePayload) =>
    request<Holiday>('/api/v1/holidays', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    }),

  updateHoliday: (id: string, payload: HolidayUpdatePayload) =>
    request<Holiday>(`/api/v1/holidays/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    }),

  deleteHoliday: (id: string) =>
    request<void>(`/api/v1/holidays/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  getSystemStatus: () => request<SystemStatus>('/api/v1/settings'),

  getCheckin: (id: string) =>
    request<Checkin>(`/api/v1/checkins/${encodeURIComponent(id)}`),

  updateCheckin: (id: string, payload: CheckinUpdatePayload) =>
    request<Checkin>(`/api/v1/checkins/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    }),

  listAdmins: () => request<AdminsListResponse>('/api/v1/admins'),

  setAdmin: (id: string, payload: AdminUpdatePayload) =>
    request<AdminListUser>(`/api/v1/admins/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    }),

  createPersonTimeOff: (slackId: string, payload: CreatePersonTimeOffPayload) =>
    request<TimeOffPayload>(
      `/api/v1/people/${encodeURIComponent(slackId)}/time_off`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      }
    ),
}
